import React, { useState, useEffect } from "react";
import {
  formatName,
  numberOfDocuments,
  formatDate,
  formatTime,
} from "@ca-dmv-radv/utilities";
import { Trans, useTranslation } from "@ca-dmv-radv/translation";
import { RadvPageWrapper } from "@ca-dmv-radv/components";
import {
  Icon,
  ButtonWrapper,
  Button,
  ICON_CHECK,
  ICON_ENVELOPE,
} from "@ca-dmv/core";
import {
  useApplication,
  useDocumentUploads,
  useAppointments,
  getCategorizedDocsByBusinessProgram,
  checkIfSkipDMVChanged,
  acknowledgeSkipDMV,
  getZ96PaymentDetails,
} from "@ca-dmv-radv/data";
import { useNavigate } from "react-router-dom";
import {
  ICON_CLOSE,
  ICON_WARNING,
  ICON_XSMALL,
} from "@ca-dmv/core/dist/js/icon";
import {
  BUTTON_STYLE_PRIMARY,
  BUTTON_STYLE_SECONDARY,
} from "@ca-dmv/core/dist/js/button";
import { Modal } from "@ca-dmv/modal";
import EmailDocument from "../dashboard/EmailDocument";
import Barcodes from "../dashboard/Barcodes";
import CustomerInfo from "./CustomerInfo";
import useTranslateDocumentName from "../dashboard/helpers/useTranslateDocumentName";
import SelectALocationButton from "../dashboard/SelectALocationButton";
import {
  isApplicationNch,
  isApplicationRnc,
  nchCategoryNameMap,
  nchStatusSubtitles,
  NCH_STATUS_ERROR,
  NCH_STATUS_PROCESSING,
  categoryIds,
  NCH_STATUS_PROCESSED,
  PAYMENT_STATUS_COMPLETED,
  BOT_STATUS_FAILURE,
  NCH_STATUS_PROCESSING_SKIP_DMV_FALSE,
  nchFinalConfirmationCategoryOrder,
  rncFinalConfirmationCategoryOrder,
} from "../constants";
import DateAndTime from "./DateAndTime";
import Location from "./Location";

function getCategoryOrder(categoryAndDocs) {
  const categoryOrder = [];
  categoryAndDocs.forEach((element) => {
    categoryOrder.push(element.categoryId);
  });
  return categoryOrder;
}

// Capitalize the first letter of each word
function formatText(string) {
  const words = string.split(" ");
  for (let i = 0; i < words.length; i += 1) {
    words[i] = words[i][0].toUpperCase() + words[i].substr(1).toLowerCase();
  }
  return words.join(" ");
}

function DocumentList({ documents, applicationType, categoryAndDocs }) {
  const { t } = useTranslation();
  let categoryOrder = null;
  const isNch = isApplicationNch(applicationType);
  const isRnc = isApplicationRnc(applicationType);
  const isNchOrRnc = isNch || isRnc;
  if (categoryAndDocs) {
    // Use order of docs on dashboard to display the docs on the final confirmation page
    if (isRnc) {
      categoryOrder = rncFinalConfirmationCategoryOrder;
    } else if (isNch) {
      categoryOrder = nchFinalConfirmationCategoryOrder;
    } else {
      categoryOrder = getCategoryOrder(categoryAndDocs);
    }
  }

  const docArray = Object.keys(documents)
    .map((key) => {
      return documents[key]
        .filter(
          (document) =>
            document && document.id !== null && document.name !== null
        )
        .map((document) => document);
    })
    .filter((arr) => arr.length > 0);

  if (categoryOrder)
    docArray.sort(
      (a, b) =>
        categoryOrder.indexOf(a[0].categoryId) -
        categoryOrder.indexOf(b[0].categoryId)
    );
  return (
    <>
      {Object.keys(docArray).map((key) => {
        return docArray[key].map((document) => {
          const docDetails = document;
          const translatedDocumentName = useTranslateDocumentName(docDetails);
          return (
            <div key={docDetails.id}>
              {isNchOrRnc &&
                docDetails.categoryId !== categoryIds.NCH_NAME_TRACING &&
                docDetails.parentId == null && (
                  <p className="text--700 text--blue-dark-2 text--md mb-24 flex flex--align-top flex--nowrap text--lh-1">
                    <Trans
                      i18nKey={nchCategoryNameMap[docDetails.categoryId]}
                      defaults={docDetails.category}
                    />
                  </p>
                )}
              {isNchOrRnc &&
                docDetails.categoryId === categoryIds.NCH_NAME_TRACING && (
                  <p className="text--700 text--blue-dark-2 text--sm mb-24 pl-24 flex flex--align-top flex--nowrap text--lh-1">
                    <Trans
                      i18nKey={nchCategoryNameMap[docDetails.categoryId]}
                      defaults={docDetails.category}
                    />
                  </p>
                )}
              {docDetails.categoryId !== categoryIds.NCH_NAME_TRACING ? (
                <p className="text--700 text--blue-dark-3 text--sm mb-24 flex flex--align-top flex--nowrap text--lh-1">
                  {docDetails.categoryId === categoryIds.SIGNATURE &&
                    docDetails?.optedDecision === "OptedOut" ? (
                    <Icon
                      icon={ICON_CLOSE}
                      className="min-width--30 mt-2"
                      size={ICON_XSMALL}
                    />
                  ) : (
                    <Icon icon={ICON_CHECK} className="min-width--30 mt-2" />
                  )}
                  <span>
                    {docDetails.categoryId === categoryIds.SIGNATURE
                      ? docDetails?.optedDecision === "OptedOut"
                        ? t("nch-screens-FinalConfirmation-optedOut", "No")
                        : t("nch-screens-FinalConfirmation-optedIn", "Yes")
                      : translatedDocumentName || docDetails.name}
                  </span>
                </p>
              ) : (
                <p className="text--700 text--blue-dark-2 text--sm mb-24 pl-24 flex flex--align-top flex--nowrap text--lh-1">
                  <Icon icon={ICON_CHECK} className="min-width--30 mt-2" />
                  <span>{translatedDocumentName || docDetails.name}</span>
                </p>
              )}
            </div>
          );
        });
      })}
    </>
  );
}

function AppointmentDetails({
  selectedAppointment,
  selectedFieldOffice,
  lang,
}) {
  const { t } = useTranslation();

  const formattedFieldOffice = {
    title: formatText(selectedFieldOffice?.title?.rendered),
    city: formatText(selectedFieldOffice?.meta.dmv_field_office_city),
    street: formatText(selectedFieldOffice?.meta.dmv_field_office_street),
    zipCode: selectedFieldOffice?.meta.dmv_field_office_zipcode,
  };

  return (
    <div>
      <h3 className="text--primary text--md">
        {t("screens-appointment-details-label", "Appointment Details")}
      </h3>
      <div className="pr-24 pb-24" style={{ display: "flex" }}>
        <div className="pr-24 mr-36">
          {selectedFieldOffice && (
            <div className="mb-0">
              <p className="text--blue-dark-1 text--sm bp-sm:text--md text--700 mb-0">
                {formattedFieldOffice?.title}
              </p>
              <p className="text--blue-dark-1 text--sm bp-sm:text--md text--700 mb-0">
                {formattedFieldOffice?.street}
              </p>
              <p className="text--blue-dark-1 text--sm bp-sm:text--md text--700 mb-0">
                {`${formattedFieldOffice?.city}, CA ${formattedFieldOffice?.zipCode}`}
              </p>
            </div>
          )}
        </div>
        <div className="pl-24">
          {selectedAppointment && (
            <div>
              <div className="mb-0">
                <p className="text--blue-dark-1 text--sm bp-sm:text--md text--700 mb-0">
                  {formatDate(selectedAppointment?.date, lang)}{" "}
                </p>
                <p className="text--blue-dark-1 text--sm bp-sm:text--md text--700 mb-0">
                  {formatTime(selectedAppointment?.time)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      <h3 className="text--primary text--red text--md">
        {t(
          "email-finalConfirmation-code-label",
          "Keep this confirmation code for your DMV appointment"
        )}
      </h3>
    </div>
  );
}

function StatusDisplay({ status }) {
  const { t } = useTranslation();
  return (
    <div className="mb-20 mt-20" style={{ display: "flex" }}>
      <p className="text--700 text--primary text--md text--black pr-12 flex flex--align-center flex--nowrap">
        <span style={{ fontWeight: "bold" }}>
          <Trans
            i18nKey="screens-FinalConfirmation-status-label"
            defaults="Status:"
          />
        </span>
      </p>
      <p className="text--700 text--black p-12 text--md flex flex--align-center flex--nowrap bg--gray-light-2">
        {t(
          `screens-FinalConfirmation-status-${status}`,
          (status ?? "").charAt(0).toUpperCase() + (status ?? "").slice(1)
        )}
        <Icon
          icon={status === "error" ? ICON_WARNING : null}
          className="min-width--30 align-center"
        />
      </p>
    </div>
  );
}

function EmailConfirmation({ email, showIcon = true }) {
  return (
    <div className="text--blue-dark-2 mt-16 flex flex--align-start bp-lg:flex--align-center flex--nowrap">
      {showIcon && (
        <Icon
          icon={ICON_ENVELOPE}
          className="mr-8 mt-5 bp-lg:mt-0 min-width--30"
        />
      )}

      <p className="mb-0">
        <Trans
          i18nKey="screens-FinalConfirmation-emailCopy"
          defaults="For your convenience, an email with a copy of this page has been sent to <span>{{email}}</span>."
          values={{ email }}
          components={{ span: <span className="text--700" /> }}
        />
      </p>
    </div>
  );
}

export default function FinalConfirmation({ title, applicationType }) {
  const { t } = useTranslation();
  const formProps = { isForm: false };
  const { application } = useApplication();
  const {
    selectedFieldOffice,
    setSelectedFieldOffice,
    selectedAppointment,
    setSelectedAppointment,
    notificationMethod,
    telNumber,
    needsAccommodations,
  } = useAppointments();
  const [addEmail, setAddEmail] = useState(false);
  const { documentUploads } = useDocumentUploads();
  const isNch = isApplicationNch(applicationType);
  const isRnc = isApplicationRnc(applicationType);
  const isNchOrRnc = isNch || isRnc;
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [categoryAndDocs, setCategoryAndDocs] = useState(null);
  const isSkipDmv = isNchOrRnc ? application?.skipDMV : false;
  const showAppointmentScheduler =
    !isSkipDmv &&
    !selectedAppointment &&
    applicationStatus !== NCH_STATUS_ERROR;
  const showAppointmentInfo = selectedAppointment && selectedFieldOffice;
  const [showSkipDMVChangedModal, setShowSkipDMVChangedModal] = useState(false);
  const paymentStatus = application?.status?.PAYMENT;
  const feeAmount = application?.feeAmount;

  const email = application?.email;
  const confirmationCode = application?.confirmationCode;
  const name = application?.application?.name;

  const legalName = formatName(name);
  const navigate = useNavigate();
  const showBarcode39 =
    !isNchOrRnc ||
    (isNchOrRnc &&
      (applicationStatus === NCH_STATUS_ERROR ||
        (!isSkipDmv && selectedAppointment)));
  const showBarcode417 = !isNchOrRnc || (!isSkipDmv && selectedAppointment);
  const [z96PaymentDetails, setZ96PaymentDetails] = useState();

  useEffect(() => {
    let isMounted = true;

    if (isNchOrRnc) {
      (async () => {
        const { success, data } = await getCategorizedDocsByBusinessProgram(
          applicationType
        );
        if (success && isMounted) {
          setCategoryAndDocs(data);
        }
      })();

      if (
        application?.status?.PAYMENT?.toUpperCase() === PAYMENT_STATUS_COMPLETED
      ) {
        (async () => {
          const { success } = await checkIfSkipDMVChanged();
          if (success && isMounted) {
            setShowSkipDMVChangedModal(true);
          }
        })();
      }
    }

    return () => {
      isMounted = false;
    };
  }, [applicationType, isNchOrRnc, application]);

  // ✅ Z96 fetch (only affects z96PaymentDetails UI block)
  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const z96Details = await getZ96PaymentDetails(applicationType);
        if (isMounted) {
          setZ96PaymentDetails(z96Details?.data);
        }
      } catch (e) {
        // intentionally ignored
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [applicationType]);

  useEffect(() => {
    if (!isSkipDmv) {
      if (application?.status?.FINISHED === true) {
        setApplicationStatus(NCH_STATUS_PROCESSED);
      } else {
        setApplicationStatus(NCH_STATUS_PROCESSING_SKIP_DMV_FALSE);
      }
    } else if (application?.status?.BOT === BOT_STATUS_FAILURE) {
      setApplicationStatus(NCH_STATUS_ERROR);
    } else if (application?.status?.FINISHED === true) {
      setApplicationStatus(NCH_STATUS_PROCESSED);
    } else {
      setApplicationStatus(NCH_STATUS_PROCESSING);
    }
  }, [application, isSkipDmv]);

  useEffect(() => {
    const existingAppointment = application?.appointment;

    if (existingAppointment) {
      setSelectedAppointment({
        date: existingAppointment?.date,
        time: existingAppointment?.time,
      });
      setSelectedFieldOffice(existingAppointment?.fieldOffice);
    }
  }, [application, setSelectedAppointment, setSelectedFieldOffice]);

  const confirmationCodeLabel = () => {
    if (!isNchOrRnc) {
      return t(
        "screens-FinalConfirmation-confirmationCode",
        "This is your confirmation code:"
      );
    }
    if (isSkipDmv) {
      return t(
        "screens-FinalConfirmation-nch-confirmationCodeLabel",
        "Confirmation Code:"
      );
    }
    return t(
      "screens-confirmation-confirmationCode",
      "Have this confirmation code ready for your appointment:"
    );
  };

  const handleSkipDMVModalClose = async () => {
    await acknowledgeSkipDMV();
    setShowSkipDMVChangedModal(false);
  };

  return (
    <RadvPageWrapper
      fullWidth
      formProps={formProps}
      showRequiredFieldSeparator={false}
      pageTitle={title}
    >
      <div className="max-width--800">
        {applicationStatus !== NCH_STATUS_ERROR && (
          <h2 className="text--md bp-md:text--lg text--700 mb-24 bp-md:mb-48">
            {t("screens-FinalConfirmation-heading", "Good News!")}
          </h2>
        )}
        {showBarcode39 && <Barcodes barcodeType={39} />}
        <h3 className="text--primary text--xmd text--black text--700 mb-24">
          {isNchOrRnc
            ? (() => {
              if (!isSkipDmv && applicationStatus === NCH_STATUS_PROCESSED) {
                return t(
                  "screens-FinalConfirmation-nameChange-photoFlow-subtitle",
                  "Your documents have been accepted and pre-approved by our online verification system."
                );
              }
              if (isSkipDmv && applicationStatus === NCH_STATUS_PROCESSING) {
                return t(
                  "screens-FinalConfirmation-nameChange-processing-senior-subtitle",
                  "Your documents have been accepted by our online verification system and your application is currently being processed. Applications typically take up to 2 business days to process."
                );
              }
              return (
                <Trans
                  i18nKey={`screens-FinalConfirmation-nameChange-${applicationStatus}-subtitle`}
                  defaults={nchStatusSubtitles[applicationStatus]}
                  values={{
                    email: application?.email,
                  }}
                />
              );
            })()
            : t(
              "screens-FinalConfirmation-subtitle",
              "Your REAL ID documents have been pre-approved by our online verification system."
            )}
        </h3>

        {isNchOrRnc && showSkipDMVChangedModal && (
          <Modal
            modalTitle={t(
              "nch-skipDMV-modal-heading",
              "Your application is currently being processed."
            )}
            titleClass="text--h3 text--primary text--blue-dark-2 text--md text--lh-1"
            onClose={handleSkipDMVModalClose}
          >
            <div>
              <p className="mb-24">
                {feeAmount === 0
                  ? t(
                    "nch-skipDMV-modal-senior-message",
                    "If you would like to make any changes, you will need to submit a new application."
                  )
                  : t(
                    "nch-skipDMV-modal-message",
                    "If you would like to make any changes, you will need to submit a new application with payment."
                  )}
              </p>
            </div>
            <div className="flex flex--row-reverse">
              <Button
                buttonClass="bp-md:mr-24 mb-24 bp-md:mb-0"
                label={t("nch-skipDMV-modal-buttonLabel", "Close")}
                onClick={handleSkipDMVModalClose}
              />
            </div>
          </Modal>
        )}

        {isNchOrRnc && paymentStatus === PAYMENT_STATUS_COMPLETED && (
          <StatusDisplay status={applicationStatus} />
        )}

        {!isNchOrRnc && selectedAppointment && (
          <p className="text--blue-dark-2 text--sm mb-24">
            {t(
              "screens-FinalConfirmation-appointmentDetails",
              "Your appointment details have been successfully submitted to DMV. If you need to cancel or reschedule your appointment, use the online "
            )}
            <a href={process.env.REACT_APP_ALL_APPOINTMENTS_LINK}>
              {t(
                "screens-FinalConfirmation-appointmentsPage",
                "Appointment Center."
              )}
            </a>
          </p>
        )}

        {isNchOrRnc &&
          !isSkipDmv &&
          applicationStatus !== NCH_STATUS_ERROR &&
          email && (
            <div className="mb-24">
              <EmailConfirmation email={email} showIcon={false} />
            </div>
          )}

        {notificationMethod && telNumber && (
          <CustomerInfo
            legalName={legalName}
            notificationMethod={notificationMethod}
            telNumber={telNumber}
          />
        )}

        {showAppointmentInfo &&
          (isNchOrRnc ? (
            <AppointmentDetails
              selectedAppointment={selectedAppointment}
              selectedFieldOffice={selectedFieldOffice}
              lang={application?.lang}
            />
          ) : (
            <div>
              <DateAndTime
                selectedAppointment={selectedAppointment}
                lang={application?.lang}
              />
              <Location
                fieldOffice={selectedFieldOffice}
                needsAccommodations={needsAccommodations}
              />
            </div>
          ))}

        <div className="flex flex--col">
          {confirmationCode && (
            <div className="w--100 max-width--500 p-20 bp-md:p-30 bg--yellow-light-2 mb-24">
              <p className="text--sm mb-0 text--blue-dark-2 text--700">
                {confirmationCodeLabel()}
              </p>
              <p className="text--md text--700 text--blue-dark-1 mb-0">
                {confirmationCode}
              </p>
            </div>
          )}
        </div>

        {showAppointmentScheduler && (
          <div className="mt-20 mb-md:mt-48 max-width--700">
            <p className="text--blue-dark-2">
              <Trans
                i18nKey="screens-confirmation-nameChange-makeAppointment"
                defaults="Click <span>Select a Location</span> below to make an appointment at a DMV location to finalize your application. Please bring the documents you have submitted with you to your appointment."
                components={{ span: <span className="text--700" /> }}
              />
            </p>
            <SelectALocationButton />
          </div>
        )}

        {z96PaymentDetails && (
          <div className="mb-24">
            <div className="text--primary text--xmd text--black text--700">
              {t("email-proofOfPayment-header")}
            </div>
            <div className="text--sm mb-0 text--black text--500">
              {t("email-proofOfPayment-subheader")}: {z96PaymentDetails}
            </div>
          </div>
        )}

        {!isNchOrRnc && (
          <div className="border--top mt-24 pt-24 mb-24">
            <h4 className="text--primary text--md text--blue-dark-2 text--700 mb-0">
              <Trans
                i18nKey="screens-FinalConfirmation-bringDocuments"
                defaults="Bring these {{numDocs}} documents with you to your DMV appointment to finalize your REAL ID."
                values={{
                  numDocs: numberOfDocuments(documentUploads),
                }}
              />
            </h4>
          </div>
        )}

        <DocumentList
          documents={documentUploads}
          applicationType={applicationType}
          categoryAndDocs={categoryAndDocs}
        />

        {!isNchOrRnc && (
          <div>
            <p className="text--blue-dark-2">
              {t(
                "screens-FinalConfirmation-printDocuments",
                "Print this page and bring it to your DMV appointment for an express experience."
              )}
            </p>
            <p className="text--blue-dark-2 mb-0">
              <Trans
                i18nKey="screens-FinalConfirmation-startHereWindow"
                defaults="When you arrive, report to the <span>Start Here</span> window."
                components={{ span: <span className="text--700" /> }}
              />
            </p>
          </div>
        )}

        {!isNchOrRnc && (
          <div className="mb-24 mt-40 ">
            <EmailConfirmation email={email} />
          </div>
        )}
      </div>

      {showBarcode417 && <Barcodes barcodeType={417} />}

      <ButtonWrapper wrapperClass="flex--col-reverse mt-40 bp-md:mt-60">
        {(!isNchOrRnc || isSkipDmv) && (
          <Button
            btnStyle={
              isNchOrRnc ? BUTTON_STYLE_SECONDARY : BUTTON_STYLE_PRIMARY
            }
            buttonClass="mb-24 bp-md:mb-0"
            label={
              isNchOrRnc
                ? t("screens-Dashboard-saveAndExit", "Save and exit")
                : t("shared.donelabel", "Done")
            }
            onClick={() => {
              if (isNchOrRnc) {
                navigate("/saved");
              } else {
                window.location = process.env.REACT_APP_THANK_YOU_LINK;
              }
            }}
          />
        )}
      </ButtonWrapper>

      {addEmail && (
        <EmailDocument
          type="final-confirmation"
          errorAlert={t(
            "errorMessages.addingEmail",
            "There was an error adding your email address. Please try again."
          )}
          onClose={() => {
            setAddEmail(false);
          }}
          actionButtonText={t("shared.continueLabel", "Continue")}
        />
      )}
    </RadvPageWrapper>
  );
}
